import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  SlideUpTransition,
  useMinimizable,
  MinimizeButton,
  MinimizedBar,
} from '../../components/MinimizableDialog';
import { FactureAvoir, FactureAvoirCreate, FactureAvoirUpdate, FactureDropdown, BonRetourDropdown, FactureAvoirItemCreate } from '../../types/factureAvoir';
import { Client } from '../../types/client';
import { facturesAvoirAPI, clientsAPI } from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import useFormDraft from '../../hooks/useFormDraft';
import { extractErrorMessage } from '../../utils/errorHandler';

interface LineItem {
  facture_item_id: number;
  designation: string;
  quantite_original: number;  // Original quantity from facture
  quantite: number;           // Quantity being returned
  prix_unitaire: number;
  total: number;
  selected: boolean;
}

interface FactureAvoirFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  avoirId?: number;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const FactureAvoirFormDialog: React.FC<FactureAvoirFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  avoirId,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const isEdit = !!avoirId;

  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [clients, setClients] = useState<Client[]>([]);
  const [clientFactures, setClientFactures] = useState<FactureDropdown[]>([]);
  const [clientBonsRetour, setClientBonsRetour] = useState<BonRetourDropdown[]>([]);

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedFacture, setSelectedFacture] = useState<FactureDropdown | null>(null);
  const [selectedBonRetour, setSelectedBonRetour] = useState<BonRetourDropdown | null>(null);
  const [tvaRate, setTvaRate] = useState<number>(20);
  const [remise, setRemise] = useState<number>(0);

  const [formData, setFormData] = useState<{
    date: string;
    bon_retour_manual: string;
    motif_retour: string;
    remarque_retour: string;
  }>({
    date: new Date().toISOString().split('T')[0],
    bon_retour_manual: '',
    motif_retour: '',
    remarque_retour: '',
  });

  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  // Draft caching — only for new avoirs (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: typeof formData;
    selectedClient: Client | null;
    selectedFacture: FactureDropdown | null;
    selectedBonRetour: BonRetourDropdown | null;
    tvaRate: number;
    remise: number;
    lineItems: LineItem[];
  }>('facture-avoir', !isEdit);

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      bon_retour_manual: '',
      motif_retour: '',
      remarque_retour: '',
    });
    setSelectedClient(null);
    setSelectedFacture(null);
    setSelectedBonRetour(null);
    setLineItems([]);
    setClientFactures([]);
    setClientBonsRetour([]);
    setTvaRate(20);
    setRemise(0);
  };

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const clientsData = await clientsAPI.getAllForDropdown();
          setClients(clientsData);

          if (isEdit && avoirId) {
            const avoir = await facturesAvoirAPI.getById(avoirId);
            populateForm(avoir, clientsData);
          } else {
            const draft = loadDraft();
            if (draft) {
              setFormData(draft.formData);
              setSelectedClient(draft.selectedClient);
              setSelectedFacture(draft.selectedFacture);
              setSelectedBonRetour(draft.selectedBonRetour);
              setTvaRate(draft.tvaRate);
              setRemise(draft.remise);
              setLineItems(draft.lineItems);
            } else {
              resetForm();
            }
          }
        } catch (err) {
          showError('Erreur lors du chargement des données');
          console.error(err);
        }
      };
      fetchData();
    }
  }, [open, avoirId, isEdit]);

  // Fetch client factures and bons retour when client is selected
  useEffect(() => {
    if (selectedClient) {
      const fetchClientData = async () => {
        try {
          const [factures, bonsRetour] = await Promise.all([
            facturesAvoirAPI.getClientFactures(selectedClient.id),
            facturesAvoirAPI.getClientBonsRetour(selectedClient.id),
          ]);
          setClientFactures(factures);
          setClientBonsRetour(bonsRetour);
        } catch (err) {
          console.error('Error fetching client data:', err);
          setClientFactures([]);
          setClientBonsRetour([]);
        }
      };
      fetchClientData();
    } else {
      setClientFactures([]);
      setClientBonsRetour([]);
    }
  }, [selectedClient]);

  // Fetch facture items when facture is selected
  useEffect(() => {
    if (selectedFacture) {
      const fetchFactureItems = async () => {
        try {
          const data = await facturesAvoirAPI.getFactureItems(selectedFacture.id);
          setTvaRate(data.tva_rate);
          setRemise(data.remise || 0);
          setLineItems(
            data.items.map(item => ({
              facture_item_id: item.id,
              designation: item.designation,
              quantite_original: Number(item.quantite),
              quantite: Number(item.quantite),
              prix_unitaire: Number(item.prix_unitaire),
              total: Number(item.total),
              selected: true,
            }))
          );
        } catch (err) {
          console.error('Error fetching facture items:', err);
          setLineItems([]);
        }
      };
      fetchFactureItems();
    } else {
      setLineItems([]);
    }
  }, [selectedFacture]);

  const populateForm = async (avoir: FactureAvoir, clientsList: Client[]) => {
    setFormData({
      date: avoir.date,
      bon_retour_manual: avoir.bon_retour_manual || '',
      motif_retour: avoir.motif_retour || '',
      remarque_retour: avoir.remarque_retour || '',
    });

    const client = clientsList.find(c => c.id === avoir.client_id);
    setSelectedClient(client || null);

    // Need to fetch client factures to set selected facture
    if (client) {
      try {
        const [factures, bonsRetour] = await Promise.all([
          facturesAvoirAPI.getClientFactures(client.id),
          facturesAvoirAPI.getClientBonsRetour(client.id),
        ]);
        setClientFactures(factures);
        setClientBonsRetour(bonsRetour);

        const facture = factures.find(f => f.id === avoir.facture_id);
        setSelectedFacture(facture || null);

        if (avoir.bon_retour_id) {
          const bonRetour = bonsRetour.find(br => br.id === avoir.bon_retour_id);
          setSelectedBonRetour(bonRetour || null);
        }
      } catch (err) {
        console.error('Error fetching client data for edit:', err);
      }
    }

    setTvaRate(Number(avoir.tva_rate));
    setRemise(Number(avoir.remise) || 0);

    if (avoir.items && avoir.items.length > 0) {
      setLineItems(
        avoir.items.map(item => ({
          facture_item_id: item.facture_item_id,
          designation: item.designation,
          quantite_original: Number(item.quantite),
          quantite: Number(item.quantite),
          prix_unitaire: Number(item.prix_unitaire),
          total: Number(item.total),
          selected: true,
        }))
      );
    }
  };

  const handleClientChange = (client: Client | null) => {
    setSelectedClient(client);
    setSelectedFacture(null);
    setSelectedBonRetour(null);
    setLineItems([]);
    setFormData(prev => ({ ...prev, bon_retour_manual: '' }));
  };

  const handleFactureChange = (facture: FactureDropdown | null) => {
    setSelectedFacture(facture);
  };

  const handleQuantiteChange = (index: number, value: string) => {
    const newItems = [...lineItems];

    if (value === '') {
      newItems[index].quantite = 0;
      newItems[index].total = 0;
      newItems[index].selected = false;
    } else if (/^\d*[.,]?\d*$/.test(value)) {
      const numValue = parseFloat(value.replace(',', '.')) || 0;
      const maxQty = newItems[index].quantite_original;
      const qty = Math.min(Math.max(0, numValue), maxQty);

      newItems[index].quantite = qty;
      newItems[index].total = Math.round(qty * newItems[index].prix_unitaire * 100) / 100;
      newItems[index].selected = qty > 0;
    }

    setLineItems(newItems);
  };

  const calculateTotalHT = () => {
    return lineItems.filter(i => i.quantite > 0).reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const calculateRemiseAmount = () => {
    return calculateTotalHT() * (remise / 100);
  };

  const calculateNetCommercial = () => {
    return calculateTotalHT() - calculateRemiseAmount();
  };

  const calculateTVAAmount = () => {
    return calculateNetCommercial() * (tvaRate / 100);
  };

  const calculateTotalNet = () => {
    return calculateNetCommercial() + calculateTVAAmount();
  };

  const handleSubmit = async () => {
    if (!selectedClient) {
      showError('Veuillez sélectionner un client');
      return;
    }

    if (!selectedFacture) {
      showError('Veuillez sélectionner une facture de base');
      return;
    }

    const selectedItems = lineItems.filter(item => item.quantite > 0);

    if (selectedItems.length === 0) {
      showError('Veuillez saisir une quantité pour au moins un article');
      return;
    }

    try {
      setLoading(true);

      const items: FactureAvoirItemCreate[] = selectedItems.map(item => ({
        facture_item_id: item.facture_item_id,
        designation: item.designation,
        quantite: item.quantite,
        prix_unitaire: item.prix_unitaire,
        total: item.total,
      }));

      if (isEdit && avoirId) {
        const updateData: FactureAvoirUpdate = {
          date: formData.date,
          bon_retour_id: selectedBonRetour?.id,
          bon_retour_manual: formData.bon_retour_manual || undefined,
          motif_retour: formData.motif_retour || undefined,
          remarque_retour: formData.remarque_retour || undefined,
          items,
        };
        await facturesAvoirAPI.update(avoirId, updateData);
        showSuccess('Facture avoir modifiée avec succès');
      } else {
        const createData: FactureAvoirCreate = {
          date: formData.date,
          client_id: selectedClient.id,
          facture_id: selectedFacture.id,
          bon_retour_id: selectedBonRetour?.id,
          bon_retour_manual: formData.bon_retour_manual || undefined,
          motif_retour: formData.motif_retour || undefined,
          remarque_retour: formData.remarque_retour || undefined,
          items,
        };
        await facturesAvoirAPI.create(createData);
        showSuccess('Facture avoir créée avec succès');
      }

      clearDraft();
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // X / ANNULER = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    resetForm();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({
        formData,
        selectedClient,
        selectedFacture,
        selectedBonRetour,
        tvaRate,
        remise,
        lineItems,
      });
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={SlideUpTransition}
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              {isEdit ? 'Modifier la Facture Avoir' : 'Nouvelle Facture Avoir'}
            </Typography>
            <Box display="flex" alignItems="center">
              <MinimizeButton color="#4CAF50" onClick={minimize} />
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Header Section */}
          <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
            Informations Générales
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                size="small"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => `${option.code} - ${option.name}`}
                value={selectedClient}
                onChange={(_, value) => handleClientChange(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={isEdit}
                renderInput={(params) => (
                  <TextField {...params} label="Client" required size="small" />
                )}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={5}>
              <Autocomplete
                options={clientFactures}
                getOptionLabel={(option) => `${option.reference} - ${Number(option.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH`}
                value={selectedFacture}
                onChange={(_, value) => handleFactureChange(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!selectedClient || isEdit}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Facture de Base"
                    required
                    size="small"
                    helperText={!selectedClient ? "Sélectionnez un client d'abord" : ""}
                  />
                )}
                size="small"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Return Info */}
          <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
            Informations de Retour
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                freeSolo
                options={clientBonsRetour}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  return `${option.bon_retour_number} - ${new Date(option.date).toLocaleDateString('fr-FR')}`;
                }}
                value={selectedBonRetour || formData.bon_retour_manual || ''}
                onChange={(_, value) => {
                  if (typeof value === 'string') {
                    setFormData({ ...formData, bon_retour_manual: value });
                    setSelectedBonRetour(null);
                  } else if (value) {
                    setSelectedBonRetour(value);
                    setFormData({ ...formData, bon_retour_manual: '' });
                  } else {
                    setSelectedBonRetour(null);
                    setFormData({ ...formData, bon_retour_manual: '' });
                  }
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input') {
                    setFormData({ ...formData, bon_retour_manual: newInputValue });
                    setSelectedBonRetour(null);
                  }
                }}
                disabled={!selectedClient}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Bon de Retour"
                    size="small"
                    placeholder="Sélectionner ou saisir..."
                  />
                )}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Motif de Retour"
                fullWidth
                size="small"
                value={formData.motif_retour}
                onChange={(e) => setFormData({ ...formData, motif_retour: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Remarque de Retour"
                fullWidth
                size="small"
                value={formData.remarque_retour}
                onChange={(e) => setFormData({ ...formData, remarque_retour: e.target.value })}
                placeholder="Remarques..."
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Items Table */}
          <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
            Articles
          </Typography>

          {lineItems.length > 0 ? (
            <>
              <TableContainer sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'background.default' }}>
                      <TableCell sx={{ width: '50%' }}>Désignation</TableCell>
                      <TableCell sx={{ width: '15%' }} align="right">Quantité</TableCell>
                      <TableCell sx={{ width: '15%' }} align="right">P.U.</TableCell>
                      <TableCell sx={{ width: '20%' }} align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.designation}</TableCell>
                        <TableCell align="right">{item.quantite_original}</TableCell>
                        <TableCell align="right">
                          {item.prix_unitaire.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                        </TableCell>
                        <TableCell align="right">
                          {item.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals Section */}
              <Box display="flex" justifyContent="flex-end">
                <Box sx={{ width: 320, backgroundColor: 'background.default', p: 2, borderRadius: 1 }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Total HT:</Typography>
                    <Typography variant="body2">
                      {calculateTotalHT().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </Typography>
                  </Box>
                  {remise > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Remise ({remise}%):</Typography>
                      <Typography variant="body2" color="error">
                        -{calculateRemiseAmount().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                      </Typography>
                    </Box>
                  )}
                  {remise > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Net Commercial:</Typography>
                      <Typography variant="body2">
                        {calculateNetCommercial().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">TVA ({tvaRate}%):</Typography>
                    <Typography variant="body2">
                      {calculateTVAAmount().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle2" fontWeight={600}>Total TTC:</Typography>
                    <Typography variant="subtitle2" fontWeight={600} color="#4CAF50">
                      {calculateTotalNet().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center', backgroundColor: 'background.default', borderRadius: 1, mb: 3 }}>
              <Typography color="text.secondary">
                Sélectionnez une facture de base pour afficher les articles
              </Typography>
            </Box>
          )}

        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': { backgroundColor: '#388E3C' },
            }}
          >
            {loading ? 'Sauvegarde...' : isEdit ? 'Modifier' : 'Valider'}
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color="#4CAF50"
        title={isEdit ? 'Modifier la Facture Avoir' : 'Nouvelle Facture Avoir'}
        onRestore={restore}
        onClose={handleClose}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </>
  );
};

export default FactureAvoirFormDialog;
